import {
  SiteContentSchema,
  CtaContent,
  ContactContent,
  AudienceSectionCopy,
  HomePageContent,
  FaqsPageContent,
  SayHelloPageContent,
  OurBuildsPageContent,
  FormSuccessPageContent,
  NotFoundPageContent,
} from './schema';

const instagramHref = 'https://www.instagram.com/mondmau_carpentry/';
const seeOurBuildsHref = '/our-builds';
const sayHelloHref = '/say-hello';
const emailAddress = 'info@mondmau.com';
const emailHref = `mailto:${emailAddress}`;
const locationHref = 'https://www.google.com/maps/place/South+London';
const PLACEHOLDER_IMAGE_1 = 'https://picsum.photos/id/1/1200/800';
const PLACEHOLDER_IMAGE_2 = 'https://picsum.photos/id/10/1200/800';
const PLACEHOLDER_IMAGE_3 = 'https://picsum.photos/id/20/1200/800';
const PLACEHOLDER_IMAGE_4 = 'https://picsum.photos/id/30/1200/800';
const PLACEHOLDER_IMAGE_5 = 'https://picsum.photos/id/40/1200/800';
const PLACEHOLDER_IMAGE_6 = 'https://picsum.photos/id/50/1200/800';

// ============================================
// CALL-TO-ACTION BUTTONS
// ============================================
const ctas: CtaContent = {
  instagram: {
    label: 'Instagram',
    href: instagramHref,
    ariaLabel: 'Message us on Instagram',
    iconName: 'FaInstagram',
  },
  seeOurBuilds: {
    label: 'See Our Builds',
    href: seeOurBuildsHref,
    ariaLabel: 'See our builds',
    iconName: 'BiGridAlt',
  },
  sayHello: {
    label: 'Message Us',
    href: sayHelloHref,
    ariaLabel: 'Message us on Say Hello page',
    iconName: 'BiMessageSquareDetail',
  },
  email: {
    label: 'Send It Over',
    href: emailHref,
    ariaLabel: 'Send us an email',
    iconName: 'BiEnvelope',
  },
};

// ============================================
// CONTACT METHODS & EMAIL
// ============================================
const contact: ContactContent = {
  email: {
    address: emailAddress,
    href: emailHref,
  },
  methods: [
    {
      id: 'email',
      title: 'Email',
      description: "Send over your idea and we'll talk through the next step with you.",
      iconName: 'BiEnvelope',
      href: emailHref,
    },
    {
      id: 'instagram',
      title: 'Instagram',
      description: "Message us on Instagram if that's easier. We'll reply and talk it through.",
      iconName: 'FaInstagram',
      href: instagramHref,
      linkLabel: 'Open Instagram',
    },
    {
      id: 'location',
      title: 'Location',
      description: 'Serving clients remotely, based in South London.',
      iconName: 'BiMapPin',
      href: locationHref,
      linkLabel: 'Show location',
    },
  ],
};

// ============================================
// SHARED SECTION CONTENT
// ============================================
const sections: { shared: AudienceSectionCopy } = {
  shared: {
    homeHero: {
      title: 'Thoughtful carpentry for real homes.',
      description:
        'We design and build fitted furniture, media walls, and custom carpentry with clear communication and practical thinking from the start.',
      ctaLabel: 'Tell Us Your Idea',
      ctaAriaLabel: 'Tell us your idea',
    },
    workshop: {
      title: 'What we make',
      description:
        'Everything starts with a conversation. We build pieces that fit your space and everyday life, then explain the options clearly as we go.',
      ctaLabel: 'See Our Builds',
      ctaAriaLabel: 'See our builds',
    },
    featuredBuild: {
      title: 'A media wall built around real daily use',
      description:
        'This project started with a simple brief: keep the room calm, keep storage practical, and make the setup work properly day to day.',
    },
    process: {
      title: 'How we work',
      description:
        'We keep the process simple: talk it through, measure properly, build carefully, and install with attention to detail.',
    },
    finalCta: {
      title: 'Got an idea in mind?',
      description:
        "Rough plans, screenshots, measurements on scraps of paper. It's all fine. Send it over and we'll talk it through together.",
      ctaLabel: 'Tell Us Your Idea',
      ctaAriaLabel: 'Tell us your idea',
    },
  },
};

// ============================================
// HOME PAGE CONTENT
// ============================================
const homePageContent: HomePageContent = {
  workshopEyebrow: 'Workshop',
  workshopItems: [
    {
      title: 'Fitted furniture',
      description:
        'Built-in storage, wardrobes, shelving, and cabinetry designed around the room and how you actually use it.',
      iconName: 'BiGridAlt',
    },
    {
      title: 'Media walls',
      description:
        'Media wall systems that keep cables and kit out of sight while still being easy to access and maintain.',
      iconName: 'BiTv',
    },
    {
      title: 'Custom carpentry',
      description:
        'Custom pieces for awkward corners, uneven walls, and unusual dimensions where off-the-shelf options do not work.',
      iconName: 'BiWrench',
    },
  ],
  featuredEyebrow: 'Featured Build',
  processEyebrow: 'Process',
  processSteps: [
    {
      stepIcon: '①',
      title: 'Conversation',
      description:
        'We start by talking through your idea, your space, and what needs to work in day-to-day life.',
    },
    {
      stepIcon: '②',
      title: 'Ideas and measurements',
      description:
        'We sketch options, take proper measurements, and explain the practical choices before anything is made.',
    },
    {
      stepIcon: '③',
      title: 'Workshop and build',
      description:
        'Your piece is built in the workshop, checked carefully, and adjusted so installation goes smoothly.',
    },
    {
      stepIcon: '④',
      title: 'Installation',
      description:
        'We install on site, scribe where needed, and make sure everything is secure, level, and properly finished.',
    },
  ],
  processFinalStep: {
    stepIcon: '⑤',
    title: 'Final details',
    description:
      'Final adjustments are made on site so the finished piece feels settled, intentional, and ready to use.',
  },
  sectionAriaLabels: {
    hero: 'Hero section',
    workshop: 'Workshop section',
    featuredBuild: 'Featured build section',
    process: 'Process section',
    finalCta: 'Final call to action',
  },
  seoPageName: 'Home',
  seoDescription:
    'Custom carpentry for real homes: fitted furniture, media walls, and bespoke pieces built properly and explained clearly.',
  seoKeywords: [
    'custom carpentry',
    'media walls',
    'fitted furniture',
    'bespoke carpentry',
    'mondmau developments',
  ],
};

// ============================================
// FAQS PAGE CONTENT
// ============================================
const faqsPageContent: FaqsPageContent = {
  headerEyebrow: 'Questions',
  headerTitle: 'Questions, answered clearly',
  headerDescription:
    "These are the questions people ask most before starting a project. If yours isn't here, message us and we'll talk it through.",
  questionsTitle: 'Questions',
  questionsDescription: 'Straight answers to the things that matter most',
  items: [
    {
      question: 'How do you start?',
      answer:
        "We begin with a conversation about what you need and how you live. There's no pressure, no sales pitch-just honest talk about your space and what's possible. From there, we'll sketch some ideas and discuss budget and timeline.",
    },
    {
      question: "What's the typical timeline?",
      answer:
        "Most projects run between eight and sixteen weeks from first meeting to completion. Fitted furniture and media walls tend to sit in the middle of that range. We'll give you a clear schedule early on so there are no surprises.",
    },
    {
      question: 'Do you work with my budget?',
      answer:
        "Yes. We build thoughtfully within whatever budget you have. We're honest about what's possible at different price points and won't push you towards unnecessary extras. Quality materials and solid craftsmanship matter more than cutting corners.",
    },
    {
      question: 'Can you work with my space?',
      answer:
        "Nearly every space has potential. Whether it's an awkward alcove, a sloped ceiling, or an open-plan room that needs definition, we've worked with all of it. We'll visit your home and figure out what makes sense.",
    },
    {
      question: 'What about materials and finishes?',
      answer:
        "We use solid wood, quality plywood, and finishes that last. We'll talk through options together-paint, stain, natural wood, or something else-based on what suits your home and how the piece will be used. No veneer shortcuts.",
    },
  ],
  finalTitle: 'Still have questions?',
  finalDescription: "Get in touch and we'll talk it through",
  seoPageName: 'FAQs',
  seoDescription:
    'Answers to common questions about the way we design, build, and install custom carpentry.',
  seoKeywords: ['faqs', 'carpentry questions', 'process', 'installation', 'budget', 'timeline'],
};

// ============================================
// SAY HELLO PAGE CONTENT
// ============================================
const sayHelloPageContent: SayHelloPageContent = {
  heroEyebrow: "Let's talk",
  heroTitle: "Tell us what you're building",
  heroDescription:
    'No pressure, just a straightforward conversation about your space and what you need. Rough ideas are completely fine.',
  contactEyebrow: 'Get in touch',
  contactTitle: 'Your project',
  contactDescription:
    "Tell us what you have in mind. We'll listen, explain the options, and help you decide the next step.",
  locationLabel: 'South London, UK',
  locationHref,
  reachEyebrow: 'Reach',
  reachTitle: 'How to find us',
  reachDescription: "We're based in South London and work across the surrounding area.",
  mapTitle: 'Studio Area',
  mapArea: 'South London',
  mapHint: 'Open map and get directions',
  finalTitle: 'Still thinking it through?',
  finalDescription:
    "Not sure where to start? That's completely fine. Send it over and we can figure it out together.",
  askButtonLabel: 'Message Us',
  chatButtonLabel: 'Send It Over',
  submitButtonLabel: 'Send',
  seoPageName: 'Contact',
  seoDescription:
    'Get in touch to talk through your idea for fitted furniture, media walls, or custom carpentry.',
  seoKeywords: ['say hello', 'contact', 'carpentry studio', 'tell us your idea'],
};

// ============================================
// OUR BUILDS PAGE CONTENT
// ============================================
const ourBuildsPageContent: OurBuildsPageContent = {
  introEyebrow: 'Our Builds',
  introTitle: 'Built work that solves real everyday problems',
  introDescription:
    'Every project starts with a conversation about how you actually use the space, what"s not working, and what you want to feel like. We build with those real needs in mind, and we"ll explain the process as we go. Select a project below to see how we brought it together, or send over your idea and we"ll talk it through.',
  viewBuildLabel: 'See How It Came Together',
  viewAllLabel: 'Tell Us Your Idea',
  introSectionAriaLabel: 'Our builds introduction',
  portfolioSectionAriaLabel: 'Build portfolio',
  items: [],
  seoPageName: 'Our Builds',
  seoDescription:
    'A portfolio of fitted furniture, home office builds, and hallway shelving projects.',
  seoKeywords: ['our builds', 'carpentry projects', 'fitted joinery', 'storage solutions'],
};

// ============================================
// Form Success Page Content
// ============================================
const formSuccessPageContent: FormSuccessPageContent = {
  title: 'Thanks for reaching out!',
  descriptionPrimary: "We've received your message and will get back to you as soon as we can.",
  descriptionSecondary:
    'In the meantime, feel free to explore our portfolio of builds or check out our FAQs for more about how we work.',
  sectionAriaLabel: 'Form submission success message',
  seoPageName: 'Message Received',
  seoDescription: "Thanks for reaching out! We've received your message and will be in touch soon.",
  seoKeywords: ['message received', 'contact success', 'thank you', 'form submission'],
};
// ============================================

// ============================================
// 404 PAGE CONTENT
// ============================================
const notFoundPageContent: NotFoundPageContent = {
  title: '404: Page Not Found',
  descriptionPrimary: "Oops! Looks like you've ventured into uncharted territory.",
  descriptionSecondary:
    "The page you're looking for seems to have gone on an adventure without us.",
  returnHomeLabel: 'Return Home',
  sectionAriaLabel: 'Page Not Found Section',
  seoPageName: '404 | Page Not Found',
  seoDescription: "Oops! Looks like you've ventured into uncharted territory.",
  seoKeywords: ['404', 'not found', 'error', 'page not found', 'missing page'],
};

// ============================================
// MAIN SITE CONTENT EXPORT
// ============================================
export const siteContent: SiteContentSchema = {
  ctas,
  contact,
  sections,
  pages: {
    home: homePageContent,
    faqs: faqsPageContent,
    sayHello: sayHelloPageContent,
    ourBuilds: ourBuildsPageContent,
    notFound: notFoundPageContent,
    formSuccess: formSuccessPageContent,
  },
};

export default siteContent;
